import Head from 'next/head'
import { useRouter } from 'next/router'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

/** Share previews need absolute addresses, so site paths are resolved against the live origin. */
const toAbsolute = (path: string): string =>
    /^https?:\/\//.test(path) ? path : `${siteConfig.url}${path.startsWith('/') ? '' : '/'}${path}`

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage
}): ReactNode => {
    const router = useRouter()
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const path = router.asPath.split(/[?#]/)[0]
    const pageUrl = toAbsolute(path === '/' ? '' : path)
    const image = ogImage ? toAbsolute(ogImage) : undefined

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <link rel='canonical' href={pageUrl} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:url' content={pageUrl} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />

            {image && <meta property='og:image' content={image} />}
            {image && <meta name='twitter:image' content={image} />}
        </Head>
    )
}
